import React, { useCallback, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Zona de alta de renglones con ciclo completo por teclado.
//
// Envuelve la fila de campos que precede a una tabla (repuestos de una OTC,
// tareas de una planilla, ítems de un movimiento de almacén) y le da el ciclo
// que hace rápida la carga repetitiva:
//
//     completar campos → Enter → renglón agregado → foco de vuelta al primero
//
// Sin esto hay que llegar al botón "Agregar" con Tab (o con el mouse) y después
// volver al primer campo por el mismo camino: dos recorridos completos de la
// fila por cada renglón. Cargando veinte repuestos seguidos, ese ida y vuelta
// es la mayor parte del tiempo de carga.
//
// Enter NO se captura cuando el desplegable de un combo está abierto: ahí la
// tecla significa "elegir esta opción" y el combo la detiene antes de que llegue
// hasta acá (ver el stopPropagation de useNavegacionTeclado). Por eso los combos
// de adentro deben ir con `saltarAlSiguiente={false}`: el salto de campo lo
// maneja el ciclo de esta zona, no el combo.
// ─────────────────────────────────────────────────────────────────────────────

const SELECTOR_CAMPOS = [
  'input:not([type="hidden"])',
  'select',
  'textarea',
].map((s) => `${s}:not([disabled]):not([tabindex="-1"])`).join(',');

export default function ZonaAltaRapida({
  children,
  onAgregar,
  // Deshabilita el ciclo (p. ej. planilla cerrada / sólo lectura).
  disabled = false,
  as: Componente = 'div',
  ...resto
}) {
  const contenedorRef = useRef(null);

  const enfocarPrimerCampo = useCallback(() => {
    const campos = Array.from(contenedorRef.current?.querySelectorAll(SELECTOR_CAMPOS) || [])
      .filter((el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    const primero = campos[0];
    if (!primero) return;
    primero.focus();
    if (typeof primero.select === 'function') {
      try { primero.select(); } catch { /* algunos tipos de input no soportan select() */ }
    }
  }, []);

  const onKeyDown = useCallback((evento) => {
    if (disabled || evento.key !== 'Enter') return;
    // En un textarea Enter escribe un salto de línea; no puede agregar el renglón.
    if (evento.target?.tagName === 'TEXTAREA') return;
    // Si el foco está en un botón, Enter ya lo está activando: no duplicar.
    if (evento.target?.tagName === 'BUTTON') return;

    evento.preventDefault();
    const resultado = onAgregar?.();
    // El alta puede ser asíncrona (p. ej. valida contra el servidor). En los dos
    // casos el foco vuelve recién cuando terminó, para no devolverlo a un campo
    // que la propia alta todavía va a limpiar.
    if (resultado && typeof resultado.then === 'function') {
      resultado.then(() => enfocarPrimerCampo()).catch(() => {});
    } else {
      // Un frame de gracia: el alta limpia los campos con setState y el foco
      // tiene que caer sobre el DOM ya actualizado.
      requestAnimationFrame(() => enfocarPrimerCampo());
    }
  }, [disabled, onAgregar, enfocarPrimerCampo]);

  return (
    <Componente ref={contenedorRef} onKeyDown={onKeyDown} {...resto}>
      {children}
    </Componente>
  );
}
